const LOGIN_KEY = "valorant-record:riot:shop-login";
const RESULT_PREFIX = LOGIN_KEY + ":result:";
const OUTCOME_PREFIX = LOGIN_KEY + ":outcome:";
const LOGIN_TTL_MS = 600000;
const LOGIN_PARAM = "valorantRecordLogin";
const RETURN_PATHS = ["/GamerSky.recordInquiry/", "/GamerSky.recordInquiry/index.html", "/db/inquiry/", "/db/inquiry/index.html"];
const RIOT_SESSION_COOKIE_NAMES = [
    "asid",
    "tdid",
    "ssid",
    "clid",
    "sub",
    "csid",
    "authenticator.sid",
    "__cflb",
    "__cf_bm",
];

function read(storage, key) {
    let raw;
    try { raw = storage.getItem(key); } catch (error) { throw new Error("RIOT_STORAGE_UNAVAILABLE"); }
    if (raw == null) return null;
    try { return JSON.parse(raw); } catch (error) { throw new Error("RIOT_STORAGE_INVALID"); }
}

function write(storage, key, value) {
    try { storage.setItem(key, JSON.stringify(value)); } catch (error) { throw new Error("RIOT_STORAGE_UNAVAILABLE"); }
}

function remove(storage, key) {
    try { storage.removeItem(key); } catch (error) { throw new Error("RIOT_STORAGE_UNAVAILABLE"); }
}

function validId(value) { return typeof value === "string" && /^[a-f0-9]{32}$/.test(value); }

function parseUrl(value) {
    try { return new URL(value); } catch (error) { throw new Error("RIOT_SHOP_LOGIN_ORIGIN"); }
}

function checkReturnUrl(returnUrl, shopUrl) {
    const target = parseUrl(returnUrl);
    const shop = parseUrl(shopUrl);
    if (!/^https?:$/.test(target.protocol) || target.origin !== shop.origin || target.username || target.password) {
        throw new Error("RIOT_SHOP_LOGIN_ORIGIN");
    }
    if (!RETURN_PATHS.includes(target.pathname)) throw new Error("RIOT_SHOP_LOGIN_PATH");
    return target;
}

function randomId() {
    if (typeof crypto === "undefined" || typeof crypto.getRandomValues !== "function") throw new Error("RIOT_SHOP_LOGIN_UNAVAILABLE");
    const values = crypto.getRandomValues(new Uint32Array(4));
    return Array.from(values, value => value.toString(16).padStart(8, "0")).join("");
}

function required(value) {
    if (typeof value !== "string" || !value.trim()) throw new Error("RIOT_SESSION_NOT_READY");
    return value.trim();
}

function readRiotSessionCookieValues(cookieText) {
    const values = new Map();
    const text = String(cookieText || "");
    for (const name of RIOT_SESSION_COOKIE_NAMES) {
        const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const match = text.match(new RegExp(`(?:^|[;,]\\s*)${escapedName}=([^;,\\s]+)`, "i"));
        if (match) values.set(name, match[1]);
    }
    return values;
}

function mergeRiotSessionCookie(previousCookie, updatedCookie) {
    const values = readRiotSessionCookieValues(previousCookie);
    for (const [name, value] of readRiotSessionCookieValues(updatedCookie)) values.set(name, value);
    return RIOT_SESSION_COOKIE_NAMES
        .filter(name => values.has(name))
        .map(name => `${name}=${values.get(name)};`)
        .join("");
}

function normalizeSession(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("RIOT_SESSION_NOT_READY");
    let accessToken = required(value.accessToken);
    try { accessToken = decodeURIComponent(accessToken); } catch (error) { throw new Error("RIOT_SESSION_INVALID"); }
    return { puuid: required(value.puuid), accessToken, entitlementsToken: required(value.entitlementsToken),
        headersCookie: typeof value.headersCookie === "string" ? value.headersCookie.trim() : "",
        loginMode: value.loginMode === "ApiMode" ? "ApiMode" : "VShopMode" };
}

function normalizePurpose(value) {
    return value === "refresh" ? "refresh" : "bind";
}

function normalizeLoginMode(value) {
    return value === "VShopMode" ? "VShopMode" : "ApiMode";
}

function normalizeRecoveryAttempt(value) {
    const attempt = Number(value);
    return Number.isInteger(attempt) && attempt > 0 ? attempt : 0;
}

function normalizeMonitorUrl(value, baseUrl) {
    if (typeof value !== "string" || !value.trim()) return "";
    try {
        const url = new URL(value, baseUrl);
        const allowedHost = url.hostname === "localhost" || url.hostname === "127.0.0.1"
            || url.hostname.endsWith(".gamersky.com") || url.hostname === "gamersky.com";
        if (!/^https?:$/.test(url.protocol) || !allowedHost
            || !url.pathname.endsWith("/valorantTracker/riot/stats/event")) {
            return "";
        }
        return url.href;
    } catch (error) {
        return "";
    }
}

function normalizeMonitorTraceId(value, fallback) {
    const traceId = typeof value === "string" ? value.trim() : "";
    return /^[A-Za-z0-9:_-]{1,64}$/.test(traceId) ? traceId : fallback;
}

function normalizeMonitorFailType(value) {
    const failType = typeof value === "string" ? value.trim().toUpperCase() : "";
    return /^[A-Z0-9_.:-]{1,64}$/.test(failType) ? failType : (failType ? "UNKNOWN_ERROR" : "");
}

function createShopRecordLogin(storage, returnUrl, shopUrl, userId, options = {}) {
    const target = checkReturnUrl(returnUrl, shopUrl);
    if (!Number.isInteger(Number(userId)) || Number(userId) <= 0) throw new Error("RIOT_BIND_REQUEST_INVALID");
    const id = options.nonce || randomId();
    if (!validId(id)) throw new Error("RIOT_SHOP_LOGIN_UNAVAILABLE");
    const previous = read(storage, LOGIN_KEY);
    if (previous && validId(previous.id)) {
        remove(storage, RESULT_PREFIX + previous.id);
        remove(storage, OUTCOME_PREFIX + previous.id);
    }
    target.searchParams.delete(LOGIN_PARAM);
    target.searchParams.set("gsAppChannelParams", "addValorantAccount");
    const createdAt = options.now == null ? Date.now() : options.now;
    const purpose = normalizePurpose(options.purpose);
    const monitorUrl = normalizeMonitorUrl(options.monitorUrl, returnUrl);
    const monitorTraceId = normalizeMonitorTraceId(options.monitorTraceId, id);
    let bindInfoId = null;
    let sourceAccountId = "";
    let recoveryAttempt = 0;
    let loginMode = "ApiMode";
    if (purpose === "refresh") {
        bindInfoId = Number(options.bindInfoId);
        if (!Number.isInteger(bindInfoId) || bindInfoId <= 0) throw new Error("RIOT_REFRESH_REQUEST_INVALID");
        sourceAccountId = required(options.sourceAccountId);
        recoveryAttempt = normalizeRecoveryAttempt(options.recoveryAttempt);
        loginMode = normalizeLoginMode(options.loginMode);
    }
    write(storage, LOGIN_KEY, {
        id,
        userId: Number(userId),
        createdAt,
        returnUrl: target.href,
        purpose,
        bindInfoId,
        sourceAccountId,
        recoveryAttempt,
        loginMode,
        monitorUrl,
        monitorTraceId,
        triggerType: purpose === "refresh"
            ? (options.triggerType === "AUTO" ? "AUTO" : "MANUAL") : "BIND",
    });
    const url = parseUrl(shopUrl);
    url.searchParams.set("operation", "tianJiaZhangHao");
    url.searchParams.set(LOGIN_PARAM, id);
    return { id, loginUrl: url.href };
}

function hasShopRecordLogin(url) { return parseUrl(url).searchParams.has(LOGIN_PARAM); }

function readShopRecordLogin(storage, url, now = Date.now()) {
    const page = parseUrl(url);
    const id = page.searchParams.get(LOGIN_PARAM);
    if (id === null) return null;
    const pending = read(storage, LOGIN_KEY);
    if (!validId(id) || !pending || pending.id !== id || !Number.isFinite(pending.createdAt)
        || now < pending.createdAt || now - pending.createdAt >= LOGIN_TTL_MS) {
        throw new Error("RIOT_SHOP_LOGIN_EXPIRED");
    }
    checkReturnUrl(pending.returnUrl, page.href);
    return pending;
}

function completeShopRecordLogin(storage, shopUrl, userId, session, now = Date.now()) {
    const pending = readShopRecordLogin(storage, shopUrl, now);
    if (!pending) return null;
    if (Number(userId) !== pending.userId) throw new Error("RIOT_ACCOUNT_MISMATCH");
    write(storage, RESULT_PREFIX + pending.id, { id: pending.id, userId: pending.userId, session: normalizeSession(session) });
    const target = checkReturnUrl(pending.returnUrl, shopUrl);
    target.searchParams.set(LOGIN_PARAM, pending.id);
    return target.href;
}

function failShopRecordLogin(storage, shopUrl, failType, now = Date.now()) {
    const pending = readShopRecordLogin(storage, shopUrl, now);
    if (!pending) return null;
    const error = normalizeMonitorFailType(failType) || "RIOT_LOGIN_FAILED";
    write(storage, RESULT_PREFIX + pending.id, { id: pending.id, userId: pending.userId, error });
    const target = checkReturnUrl(pending.returnUrl, shopUrl);
    target.searchParams.set(LOGIN_PARAM, pending.id);
    return target.href;
}

function consumeShopRecordLogin(storage, pageUrl, userId, now = Date.now()) {
    const pending = readShopRecordLogin(storage, pageUrl, now);
    if (!pending) return null;
    if (Number(userId) !== pending.userId) throw new Error("RIOT_ACCOUNT_MISMATCH");
    const result = read(storage, RESULT_PREFIX + pending.id);
    if (!result || result.id !== pending.id || result.userId !== pending.userId) throw new Error("RIOT_SESSION_NOT_READY");
    if (result.error) {
        const failType = normalizeMonitorFailType(result.error) || "RIOT_LOGIN_FAILED";
        remove(storage, RESULT_PREFIX + pending.id);
        remove(storage, LOGIN_KEY);
        throw new Error(failType);
    }
    const session = normalizeSession(result.session);
    write(storage, OUTCOME_PREFIX + pending.id, {
        id: pending.id,
        userId: pending.userId,
        purpose: normalizePurpose(pending.purpose),
        bindInfoId: pending.bindInfoId || null,
        sourceAccountId: pending.sourceAccountId || "",
        state: "processing",
        startedAt: now,
        updatedAt: now,
    });
    const cleanUrl = parseUrl(pageUrl);
    cleanUrl.searchParams.delete(LOGIN_PARAM);
    remove(storage, RESULT_PREFIX + pending.id);
    remove(storage, LOGIN_KEY);
    return {
        session,
        id: pending.id,
        userId: pending.userId,
        operationStartedAt: pending.createdAt,
        cleanUrl: cleanUrl.href,
        purpose: normalizePurpose(pending.purpose),
        bindInfoId: pending.bindInfoId || null,
        sourceAccountId: pending.sourceAccountId || "",
        recoveryAttempt: normalizeRecoveryAttempt(pending.recoveryAttempt),
        loginMode: normalizeLoginMode(pending.loginMode),
        monitorUrl: pending.monitorUrl || "",
        monitorTraceId: normalizeMonitorTraceId(pending.monitorTraceId, pending.id),
        triggerType: pending.triggerType || (normalizePurpose(pending.purpose) === "refresh" ? "MANUAL" : "BIND"),
    };
}

function reportShopRecordLoginStage(storage, pageUrl, stage, details = {}) {
    const pending = readShopRecordLogin(storage, pageUrl);
    if (!pending || !pending.monitorUrl || !/^[A-Z0-9_]{1,64}$/.test(stage)) return false;
    const payload = {
        traceId: normalizeMonitorTraceId(pending.monitorTraceId, pending.id),
        stage,
        gamerskyUserId: pending.userId,
        scene: normalizePurpose(pending.purpose) === "refresh" ? "REFRESH" : "BIND",
        triggerType: pending.triggerType || (normalizePurpose(pending.purpose) === "refresh" ? "MANUAL" : "BIND"),
        occurredAtEpochMs: Date.now(),
        elapsedMs: Math.max(0, Date.now() - pending.createdAt),
        success: typeof details.success === "boolean" ? details.success : null,
        failType: normalizeMonitorFailType(details.failType),
    };
    try {
        if (typeof fetch !== "function") return false;
        Promise.resolve(fetch(pending.monitorUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json;charset=UTF-8" },
            body: JSON.stringify(payload),
            keepalive: true,
        })).catch(() => {});
        return true;
    } catch (error) {
        return false;
    }
}

function readShopRecordLoginOutcome(storage, id, userId, now = Date.now()) {
    if (!validId(id)) return null;
    const outcome = read(storage, OUTCOME_PREFIX + id);
    if (!outcome) return null;
    if (outcome.id !== id || !Number.isFinite(outcome.startedAt)
        || now < outcome.startedAt || now - outcome.startedAt >= LOGIN_TTL_MS) {
        remove(storage, OUTCOME_PREFIX + id);
        return null;
    }
    if (Number(userId) !== outcome.userId) throw new Error("RIOT_ACCOUNT_MISMATCH");
    return outcome;
}

function completeShopRecordLoginOutcome(storage, id, userId, succeeded, detail = {}, now = Date.now()) {
    const outcome = readShopRecordLoginOutcome(storage, id, userId, now);
    if (!outcome) return false;
    write(storage, OUTCOME_PREFIX + id, {
        ...outcome,
        state: succeeded ? "succeeded" : "failed",
        bindInfoId: detail.bindInfoId || outcome.bindInfoId || null,
        sourceAccountId: detail.sourceAccountId || outcome.sourceAccountId || "",
        error: succeeded ? "" : (typeof detail.error === "string" ? detail.error : "RIOT_LOGIN_RETURN_FAILED"),
        updatedAt: now,
    });
    return true;
}

function cancelShopRecordLogin(storage, id) {
    if (!validId(id)) return;
    const pending = read(storage, LOGIN_KEY);
    if (pending && pending.id === id) {
        remove(storage, LOGIN_KEY);
        remove(storage, OUTCOME_PREFIX + id);
    }
    remove(storage, RESULT_PREFIX + id);
}

module.exports = { createShopRecordLogin, hasShopRecordLogin, readShopRecordLogin,
    completeShopRecordLogin, failShopRecordLogin, consumeShopRecordLogin, cancelShopRecordLogin,
    readShopRecordLoginOutcome, completeShopRecordLoginOutcome, mergeRiotSessionCookie,
    reportShopRecordLoginStage };
